import { Redirect, useRouter } from 'expo-router'
import { useState, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Input } from '@/src/elements/input/Input'
import { PlaceholderLine } from '@/src/elements/placeholder'
import { Typography } from '@/src/elements/text/Typography'
import { useLoginDetails } from '@/src/features/auth/services/useLoginDetails'
import { useResetPasswordMutation } from '@/src/features/auth/services/useResetPasswordMutation'
import { ErrorMessage } from '@/src/general/components/ErrorMessage'
import { HTTPException } from '@/src/general/errors/HTTPException'
import { parseApiErrors } from '@/src/general/utilities/fetcher/parseApiError'
import { filterSearchParams } from '@/src/general/utils/filterSearchParams'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { getIsLoggedIn } from '../../utils/getIsLoggedIn'

import LogoSvg from '@/assets/icons/hozana.svg'

const getPasswordErrorMessageKey = (error: Error | null) => {
  const parsedErrors = parseApiErrors(error, ['password', 'email', 'token'])
  return parsedErrors?.find(([field]) => field === 'password')?.[1]
}

export const AuthCompleteScreen = ({ searchParams }: TSearchParamsParam): ReactNode => {
  const [password, setPassword] = useState('')
  const { t } = useTranslate()
  const router = useRouter()
  const { routes } = useRoutes()
  const filteredSearchParams = filterSearchParams(searchParams)
  const redirectTo = filteredSearchParams.redirectTo || routes['prayer-space']()
  const { initialized, name, email, resetPasswordToken } = useLoginDetails(searchParams)

  const { mutate: resetPassword, error, isPending } = useResetPasswordMutation(redirectTo)

  if (getIsLoggedIn()) {
    return <Redirect href={redirectTo ?? routes['prayer-space']()} />
  }

  if (initialized || (error instanceof HTTPException && [401, 404].includes(error.status))) {
    return <Redirect href={routes.connect(redirectTo)} />
  }

  const handleSubmit = () => {
    if (!email || !resetPasswordToken) return
    resetPassword({ email, password, token: resetPasswordToken })
  }

  const handlePasswordChange = (text: string) => setPassword(text)

  const passwordErrorMessageKey = getPasswordErrorMessageKey(error)

  return (
    <View className="h-full w-full">
      <View className="items-center py-4">
        <LogoSvg className="h-16 w-52" />
      </View>
      {name ? (
        <Typography bold size="xl" center className="pb-3 pt-6">
          <FormattedMessage id="user.wizard.welcome.user" values={{ name }} />
        </Typography>
      ) : (
        <PlaceholderLine size="xl" className="mx-auto mb-3 mt-6 w-80 animate-pulse" />
      )}
      <View className="gap-4 p-4">
        <Typography center bold>
          <FormattedMessage id="auth.complete.choose-password" values={{ br: () => <br /> }} />
        </Typography>
        <Input
          placeholder={t('words.password.placeholder')}
          secureTextEntry
          value={password}
          onChangeText={handlePasswordChange}
          onSubmitEditing={handleSubmit}
          isError={!!passwordErrorMessageKey}
          errorMessage={passwordErrorMessageKey ? t(passwordErrorMessageKey) : undefined}
        />
        {error && !passwordErrorMessageKey && <ErrorMessage error={error} defaultErrorKey="common.form.error.fail" />}
        <Button
          colors="primary"
          variant="solid"
          className="w-full"
          onPress={handleSubmit}
          isDisabled={isPending || !email || !resetPasswordToken || !password}
        >
          <FormattedMessage id="words.i-continue" />
        </Button>
        <View className="py-4">
          <Typography bold contrast="low" center>
            <FormattedMessage id="words.your-email-address" />
          </Typography>
          {email ? <Typography center>{email}</Typography> : <PlaceholderLine className="mx-auto w-60 animate-pulse" />}
          <Button
            variant="light"
            colors="primary"
            className="my-2 w-full"
            onPress={() => router.push(routes.connect(redirectTo))}
          >
            <FormattedMessage id="auth.complete.not-my-email" />
          </Button>
        </View>
      </View>
    </View>
  )
}
